import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePWA } from '@/hooks/usePWA';
import {
    ArrowRight,
    Battery,
    CheckCircle,
    Download,
    Shield,
    Smartphone,
    Star,
    Wifi,
    X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface InstallPromptProps {
  className?: string;
  onInstall?: () => void;
  onDismiss?: () => void;
}

export const InstallPrompt: React.FC<InstallPromptProps> = ({ 
  className = '',
  onInstall,
  onDismiss 
}) => {
  const { 
    isInstallable, 
    isInstalled, 
    isStandalone, 
    installPWA,
    isOnline 
  } = usePWA({
    enableNotifications: true,
    enableBackgroundSync: true,
    enablePushNotifications: true
  });

  const [isVisible, setIsVisible] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Verificar se deve mostrar o prompt
  useEffect(() => {
    const shouldShow = isInstallable && 
                      !isInstalled && 
                      !isStandalone && 
                      !dismissed &&
                      isOnline;
    
    setIsVisible(shouldShow);
  }, [isInstallable, isInstalled, isStandalone, dismissed, isOnline]);

  // Verificar se foi dispensado anteriormente
  useEffect(() => {
    const dismissedState = localStorage.getItem('pwa-install-dismissed');
    if (dismissedState === 'true') {
      setDismissed(true);
    }
  }, []);

  const handleInstall = async () => {
    setIsInstalling(true);
    
    try {
      const success = await installPWA();
      if (success) {
        setIsVisible(false);
        onInstall?.();
      }
    } catch (error) {
      console.error('Erro ao instalar PWA:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', 'true');
    onDismiss?.();
  };

  const handleLater = () => {
    setIsVisible(false);
    // Remover após 7 dias
    setTimeout(() => {
      localStorage.removeItem('pwa-install-dismissed');
    }, 7 * 24 * 60 * 60 * 1000);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`fixed bottom-20 md:bottom-4 left-4 right-4 z-50 max-w-md mx-auto ${className}`}>
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-2xl animate-slide-up">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <Download className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg font-bold text-slate-900">
                  Instalar TaMoAki
                </CardTitle>
                <Badge variant="secondary" className="text-xs">
                  PWA Disponível
                </Badge>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600 leading-relaxed">
            Instale o TaMoAki no seu dispositivo para uma experiência mais rápida e conveniente.
          </p>
          
          {/* Benefícios */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-slate-800">Benefícios:</h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Smartphone className="w-3 h-3 text-blue-500" />
                <span>Acesso rápido</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Wifi className="w-3 h-3 text-green-500" />
                <span>Funciona offline</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Battery className="w-3 h-3 text-yellow-500" />
                <span>Menos bateria</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Shield className="w-3 h-3 text-purple-500" />
                <span>Mais seguro</span>
              </div>
            </div>
          </div>
          
          {/* Estatísticas */}
          <div className="bg-white/50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-600">Usuários que instalaram:</span>
              <span className="font-semibold text-slate-800">+1,200</span>
            </div>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} className="w-3 h-3 text-yellow-400 fill-current" />
              ))}
              <span className="text-xs text-slate-600 ml-1">4.8/5</span>
            </div>
          </div>
          
          {/* Botões de ação */}
          <div className="flex gap-2">
            <Button
              onClick={handleInstall}
              disabled={isInstalling}
              className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold"
            >
              {isInstalling ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Instalando...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  <span>Instalar</span>
                </div>
              )}
            </Button>
            
            <Button
              variant="outline"
              onClick={handleLater}
              className="px-4 text-slate-600 hover:text-slate-800"
            >
              Depois
            </Button>
          </div>
          
          {/* Instruções */}
          <div className="text-xs text-slate-500 text-center">
            <p>Após a instalação, você encontrará o app na tela inicial</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Componente de status de instalação
export const InstallStatus: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { isInstalled, isStandalone, isOnline } = usePWA();

  if (!isInstalled) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 text-xs ${className}`}>
      <CheckCircle className="w-4 h-4 text-green-500" />
      <span className="text-green-700 font-medium">
        {isStandalone ? 'App instalado' : 'PWA ativo'}
      </span>
      {!isOnline && (
        <Badge variant="secondary" className="text-xs">
          Offline
        </Badge>
      )}
    </div>
  );
};

// Componente de atualização disponível
export const UpdateAvailable: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { updateAvailable, updatePWA } = usePWA();
  const [isUpdating, setIsUpdating] = useState(false);

  if (!updateAvailable) {
    return null;
  }

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await updatePWA();
    } catch (error) {
      console.error('Erro ao atualizar:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className={`fixed top-4 right-4 z-50 ${className}`}>
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
              <ArrowRight className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-green-800">
                Atualização disponível
              </p>
              <p className="text-xs text-green-600">
                Nova versão do TaMoAki
              </p>
            </div>
            <Button
              size="sm"
              onClick={handleUpdate}
              disabled={isUpdating}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isUpdating ? 'Atualizando...' : 'Atualizar'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Export default para compatibilidade com imports default
export default InstallPrompt;